import { useWebsiteSettings } from 'client/store'
import React, { useEffect, useState } from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { useMutationWithValidation } from 'src/editor/api/hooks/use-api-mutation-with-validation'
import { websitePagesQuery } from 'src/editor/api/queries/website-pages-query'
import { getWebsiteId } from 'src/editor/store/website-settings/website-settings-selector'
import { ConfirmationDialogWindow } from 'src/editor/shared/ui-lib/modal-window/dialog-window/confirmation-dialog-window'
import { removeWebsitePageLocale } from 'src/editor/api/website-pages-api'

interface RemovePageLocaleProps {
  websitePageSetId: number
  localeToRemove: PageLocaleEnum | null
  onClose: () => void
}

export const RemovePageLocale = ({
  websitePageSetId,
  localeToRemove,
  onClose,
}: RemovePageLocaleProps) => {
  const { t } = useTypedTranslation()
  const [isOpened, setIsOpened] = useState(false)

  const websiteId = useWebsiteSettings(getWebsiteId) as number

  const handleClose = () => {
    setIsOpened(false)
    onClose()
  }

  const { fetcher, isLoading } = useMutationWithValidation({
    requestFunction: removeWebsitePageLocale,
    successToastMessage: t(
      'page_settings.locales.remove_locale.removed_successfully',
    ),
    handleFinally: () => {
      handleClose()
    },
    setQueryDataArgsOnSuccess: websitePagesQuery.setQueryDataArgs(
      [websiteId],
      prevData => ({
        ...prevData,
        pages: prevData.pages.map(websitePageSet =>
          websitePageSet.id === websitePageSetId
            ? {
                ...websitePageSet,
                locales: websitePageSet.locales.filter(
                  websitePage => websitePage.locale !== localeToRemove,
                ),
              }
            : websitePageSet,
        ),
      }),
    ),
  })

  useEffect(() => {
    if (localeToRemove) {
      setIsOpened(true)
    }
  }, [localeToRemove])

  const handleRemoveLocale = () => {
    fetcher({
      websitePageSetId,
      locale: localeToRemove as PageLocaleEnum,
    })
  }

  return (
    <ConfirmationDialogWindow
      isOpened={isOpened}
      onClose={handleClose}
      title={t('page_settings.locales.remove_locale.confirmation_window.title')}
      description={t(
        'page_settings.locales.remove_locale.confirmation_window.description',
      )}
      cancelButtonProps={{
        children: t('global.cancel'),
        onClick: handleClose,
      }}
      confirmButtonProps={{
        isLoading,
        onClick: handleRemoveLocale,
        children: t('global.remove'),
      }}
    />
  )
}
